import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import { cancelTerminalPayment, type CancelTerminalPaymentRequest } from 'api/payment'
import config from 'constants/config'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { trackCheckoutObservabilityEvent } from 'analytics/snowplow/checkoutAnalytics'

const ErrorButton = styled(TextButton)`
  &.kind-primary.variant-default {
    --button-color: ${props => props.theme.palette.messaging.critical.normalContrast};
    --button-background-color: ${props => props.theme.palette.messaging.critical.normalBackground};
    --button-border-color: ${props => props.theme.palette.messaging.critical.normalBackground};
    --button-disabled-color: ${props => props.theme.palette.neutral.surface.lightContrastAlt};
    --button-disabled-background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
    --button-disabled-border-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    --button-hover-color: ${props => props.theme.palette.messaging.critical.normalContrast};
    --button-hover-background-color: ${props => props.theme.palette.messaging.critical.darkBackground};
    --button-hover-border-color: ${props => props.theme.palette.messaging.critical.darkBackground};
    --button-active-color: ${props => props.theme.palette.messaging.critical.normalContrast};
    --button-active-background-color: ${props => props.theme.palette.messaging.critical.darkBackground};
    --button-active-border-color: ${props => props.theme.palette.messaging.critical.darkBackground};
    --button-focus-color: ${props => props.theme.palette.messaging.critical.normalContrast};
    --button-focus-border-color: ${props => props.theme.palette.messaging.critical.normalBackground};
    --button-focus-background-color: ${props => props.theme.palette.messaging.critical.normalBackground};

    body.${REBRAND_PALETTE_CLASS} & {
      --button-background: var(--palette-messaging-critical-normal-background);
      --button-foreground: var(--palette-messaging-critical-normal-foreground);
      --button-contrast: var(--palette-messaging-critical-normal-contrast);
    }
  }
`

interface Props {
  orderId: string
  currencyCode: string
  terminalReaderId: string
}

function TerminalCancelButton({ orderId, currencyCode, terminalReaderId }: Props) {
  const intl = useIntl()

  const cancellationErrorHeading = intl.formatMessage({
    id: 'IUtlVJXOHA9QIqkKUHfq',
    defaultMessage: 'Cancellation Error',
    description: 'Snackbar heading for terminal payment cancellation error',
  })

  const paymentCancelledHeading = intl.formatMessage({
    id: 'kudASeDEhXWtQp3JzVoF',
    defaultMessage: 'Payment Cancelled',
    description: 'Snackbar heading when terminal payment is successfully cancelled',
  })

  const unableToCancelHeading = intl.formatMessage({
    id: 'nxJTqHA2eaZ_Xz6u8YOf',
    defaultMessage: 'Unable to Cancel',
    description: 'Snackbar heading when terminal payment cancellation fails',
  })

  const failedCancelMsg = intl.formatMessage({
    id: 'AUijBknxR51pvljyPzm1',
    defaultMessage: 'Failed to cancel terminal payment',
    description: 'Default error message when terminal payment cancellation throws an exception',
  })

  const handleCancelTerminalPayment = useCallback(async() => {
    try {
      const response = await cancelTerminalPayment({
        orderId,
        readerId: terminalReaderId,
        currency: currencyCode as CancelTerminalPaymentRequest['currency'],
        brand: config.BRAND as CancelTerminalPaymentRequest['brand'],
      })
      const kind = response.result.cancelled ? 'success' : 'warning'
      trackCheckoutObservabilityEvent({
        subject: 'payment',
        action: response.result.cancelled ? 'declined' : 'validation_failed',
        type: 'operational',
        reasonCode: response.result.cancelled ? 'payment_cancelled' : 'unknown',
        isBlocking: !response.result.cancelled,
        canContinue: response.result.cancelled,
      })
      showSnackbar(response.message, kind, {
        heading: response.result.cancelled ? paymentCancelledHeading : unableToCancelHeading,
      })
    } catch(error: unknown) {
      trackCheckoutObservabilityEvent({
        subject: 'payment',
        action: 'validation_failed',
        type: 'operational',
        reasonCode: 'network_error',
        isBlocking: true,
        canContinue: false,
      })
      const message = error instanceof Error ? error.message : undefined
      showSnackbar(message || failedCancelMsg, 'critical', { heading: cancellationErrorHeading })
    }
  }, [orderId, currencyCode, terminalReaderId, cancellationErrorHeading, paymentCancelledHeading, unableToCancelHeading, failedCancelMsg])

  return (
    <ErrorButton kind="primary" onClick={handleCancelTerminalPayment} size="medium">
      {intl.formatMessage({
        id: 'fPIzHXCrO8_JgSfcqAMR',
        defaultMessage: 'Cancel Payment',
        description: 'Button to cancel a Stripe terminal payment during processing',
      })}
    </ErrorButton>
  )
}

export default TerminalCancelButton
